/* eslint-disable react/no-array-index-key */
import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios, { API } from '../utils/api';
import { useCommunityAuth } from '../context/CommunityAuthContext';
import CommunityTopHeader from '../components/community/CommunityTopHeader';
import { ArrowLeft, Calendar, MapPin, Plus, Trash2, Megaphone, HelpCircle, BarChart3, Star, ImagePlus, X, Check } from 'lucide-react';
import notify from '../utils/notify';

const COMM = `${API}/community`;
const TEAL = '#4DB6AC';

const META = {
  announcement: { label: 'Announcement', Icon: Megaphone, hint: 'Broadcast a notice to the community.' },
  quiz: { label: 'Quiz', Icon: HelpCircle, hint: 'Ask a question with correct answers — users earn stars.' },
  poll: { label: 'Poll', Icon: BarChart3, hint: 'Ask a question, no right answer — gauge community opinion.' },
};

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: 14, outline: 'none', background: '#FAFAFA' };

export default function CommunityCreateActionPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { token } = useCommunityAuth();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // Type is locked in by the chooser modal. Fallback to announcement if missing.
  const initialType = (params.get('type') || 'announcement').toLowerCase();
  const [type, setType] = useState(['announcement', 'quiz', 'poll'].includes(initialType) ? initialType : 'announcement');
  const meta = META[type];

  const [form, setForm] = useState({
    title: '',
    description: '',
    image_url: '',
    location: '',
    start_date: '',
    end_date: '',
    question: '',
    options: ['', ''],
    correct: new Set(),  // Set<number> for quiz
    reward_stars: 5,
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleCorrect = (idx) => {
    setForm(f => {
      const next = new Set(f.correct);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return { ...f, correct: next };
    });
  };

  const addOption = () => {
    if (form.options.length >= 4) return;
    set('options', [...form.options, '']);
  };

  const removeOption = (idx) => {
    if (form.options.length <= 2) return;
    const next = form.options.filter((_, i) => i !== idx);
    // re-index correct answers
    const remapped = new Set();
    [...form.correct].forEach(i => { if (i !== idx) remapped.add(i > idx ? i - 1 : i); });
    setForm(f => ({ ...f, options: next, correct: remapped }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      notify.error('Only JPG and PNG images are supported.'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      notify.error('Image too large — max 5MB.'); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${COMM}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      set('image_url', res.data?.url || res.data?.image_url || '');
    } catch (err) {
      notify.error(err.response?.data?.detail || 'Image upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const validate = () => {
    if (type === 'announcement') {
      if (!form.title.trim()) return 'Title is required.';
    } else {
      if (!form.question.trim()) return 'Question is required.';
      const opts = form.options.map(o => o.trim()).filter(Boolean);
      if (opts.length < 2) return 'Provide at least 2 options.';
      if (opts.length > 4) return 'No more than 4 options.';
      if (type === 'quiz' && form.correct.size === 0) return 'Mark at least one correct answer.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { notify.error(err); return; }
    setSubmitting(true);
    try {
      const opts = form.options.map(o => o.trim()).filter(Boolean);
      const payload = {
        action_type: type,
        title: form.title.trim() || (form.question || '').trim().slice(0, 80),
        description: form.description.trim() || null,
        image_url: form.image_url || null,
        location: form.location.trim() || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        question: (type === 'quiz' || type === 'poll') ? form.question.trim() : null,
        options: (type === 'quiz' || type === 'poll') ? opts : [],
        correct_indices: type === 'quiz' ? [...form.correct].filter(i => i < opts.length) : [],
        reward_stars: type === 'quiz' ? (parseInt(form.reward_stars, 10) || 5) : 0,
      };
      await axios.post(`${COMM}/actions`, payload, authHeaders);
      notify.success(`${meta.label} created`);
      navigate('/community');
    } catch (err) {
      notify.error(err.response?.data?.detail || 'Failed to create. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = meta.Icon;
  const showQA = type === 'quiz' || type === 'poll';

  // Type selector at top — also allows switching after landing on the page.
  const typeChips = useMemo(() => Object.entries(META), []);

  return (
    <div className="page-container">
      <CommunityTopHeader />
      <div className="content-wrapper" style={{ background: '#F8FAF9' }}>
        <div className="content-section" style={{ padding: '0 16px 24px' }}>
          {/* Back row */}
          <button type="button" onClick={() => navigate(-1)}
            data-testid="action-back-btn"
            style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: 6, color: '#6B7280', fontSize: 13, fontWeight: 500, cursor: 'pointer', margin: '12px 0', padding: 0 }}>
            <ArrowLeft size={16} /> Back
          </button>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: `${TEAL}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon size={22} color={TEAL} />
            </div>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1F2937', margin: 0 }}>Create {meta.label}</h1>
              <p style={{ fontSize: 13, color: '#6B7280', margin: '2px 0 0' }}>{meta.hint}</p>
            </div>
          </div>

          {/* Type chips */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 18 }} data-testid="action-type-chips">
            {typeChips.map(([key, m]) => (
              <button key={key} type="button" onClick={() => setType(key)} data-testid={`type-${key}`}
                style={{
                  flex: 1, padding: '10px 8px', borderRadius: 10,
                  border: `1.5px solid ${type === key ? TEAL : '#E5E7EB'}`,
                  background: type === key ? TEAL : '#FFF',
                  color: type === key ? '#FFF' : '#374151',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  transition: 'all 0.15s',
                }}>
                <m.Icon size={14} /> {m.label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} style={{ background: '#FFF', borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column', gap: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>

            {/* ── Announcement specific ── */}
            {type === 'announcement' && (
              <>
                <div>
                  <label style={labelStyle}>Title *</label>
                  <input style={inputStyle} value={form.title} onChange={e => set('title', e.target.value)} data-testid="input-title" placeholder="Heads up to the community" required />
                </div>
                <div>
                  <label style={labelStyle}>Description</label>
                  <textarea rows={4} style={{ ...inputStyle, resize: 'vertical' }} value={form.description} onChange={e => set('description', e.target.value)} data-testid="input-description" placeholder="Add the details..." />
                </div>

                {/* Image upload */}
                <div>
                  <label style={labelStyle}><ImagePlus size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Image</label>
                  {form.image_url ? (
                    <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden' }}>
                      <img src={form.image_url} alt="preview" style={{ width: '100%', height: 160, objectFit: 'cover' }} />
                      <button type="button" onClick={() => set('image_url', '')}
                        data-testid="remove-image-btn"
                        style={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px', borderRadius: 10, border: `1.5px dashed ${TEAL}`, background: '#f0faf9', color: TEAL, fontSize: 13, fontWeight: 600, cursor: uploading ? 'wait' : 'pointer' }}>
                      <input type="file" accept="image/jpeg,image/png" data-testid="input-image" disabled={uploading} onChange={handleImageUpload} style={{ display: 'none' }} />
                      <ImagePlus size={15} /> {uploading ? 'Uploading…' : 'Upload image (JPG/PNG, max 5MB)'}
                    </label>
                  )}
                </div>

                <div>
                  <label style={labelStyle}><MapPin size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Location (optional)</label>
                  <input style={inputStyle} value={form.location} onChange={e => set('location', e.target.value)} data-testid="input-location" placeholder="Where this is relevant" />
                </div>
              </>
            )}

            {/* ── Quiz / Poll specific ── */}
            {showQA && (
              <>
                <div>
                  <label style={labelStyle}>Question *</label>
                  <textarea rows={2} style={{ ...inputStyle, resize: 'vertical' }} value={form.question} onChange={e => set('question', e.target.value)} data-testid="input-question" placeholder={type === 'quiz' ? 'e.g. Which is the cleanest beach near us?' : 'e.g. What should our next event be?'} required />
                </div>

                <div>
                  <label style={labelStyle}>Options ({form.options.length}/4)</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {form.options.map((opt, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {type === 'quiz' && (
                          <button type="button" onClick={() => toggleCorrect(i)}
                            data-testid={`option-correct-${i}`}
                            style={{
                              width: 28, height: 28, borderRadius: 6,
                              border: `1.5px solid ${form.correct.has(i) ? TEAL : '#D1D5DB'}`,
                              background: form.correct.has(i) ? TEAL : '#FFF',
                              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }} title="Mark as correct">
                            {form.correct.has(i) && <Check size={16} color="#FFF" />}
                          </button>
                        )}
                        <input style={inputStyle} value={opt}
                          onChange={e => { const next = [...form.options]; next[i] = e.target.value; set('options', next); }}
                          data-testid={`input-option-${i}`}
                          placeholder={`Option ${i + 1}`} />
                        {form.options.length > 2 && (
                          <button type="button" onClick={() => removeOption(i)}
                            data-testid={`remove-option-${i}`}
                            style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #E5E7EB', background: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                            <Trash2 size={14} color="#9CA3AF" />
                          </button>
                        )}
                      </div>
                    ))}
                    {form.options.length < 4 && (
                      <button type="button" onClick={addOption}
                        data-testid="add-option-btn"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', borderRadius: 10, border: `1.5px dashed ${TEAL}`, background: '#f0faf9', color: TEAL, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                        <Plus size={14} /> Add option
                      </button>
                    )}
                  </div>
                  {type === 'quiz' && (
                    <div style={{ marginTop: 6, fontSize: 11, color: '#9CA3AF' }}>Tap the checkbox to mark one or more correct answers.</div>
                  )}
                </div>

                {type === 'quiz' && (
                  <div>
                    <label style={labelStyle}><Star size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Reward stars</label>
                    <input type="number" min="0" max="500" style={inputStyle} value={form.reward_stars}
                      onChange={e => set('reward_stars', e.target.value)}
                      data-testid="input-reward-stars" />
                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Awarded on correct answer. Default 5.</div>
                  </div>
                )}
              </>
            )}

            {/* Dates — shared by all three types */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}><Calendar size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Start date</label>
                <input type="date" style={inputStyle} value={form.start_date} onChange={e => set('start_date', e.target.value)} data-testid="input-start-date" />
              </div>
              <div>
                <label style={labelStyle}><Calendar size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />End date</label>
                <input type="date" style={inputStyle} value={form.end_date} onChange={e => set('end_date', e.target.value)} data-testid="input-end-date" />
              </div>
            </div>

            <button type="submit" disabled={submitting}
              data-testid="submit-action-btn"
              style={{ width: '100%', padding: '14px', background: submitting ? '#9CA3AF' : TEAL, color: '#FFF', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer', boxShadow: '0 4px 12px rgba(77,182,172,0.25)', marginTop: 4 }}>
              {submitting ? 'Creating…' : `Create ${meta.label}`}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
